import {
    Box,
    Button,
    FormControlLabel,
    Grid,
    Paper,
    Switch,
    TextField,
    Typography,
} from "@material-ui/core"
import { PersonOutline } from "@material-ui/icons"
import React, { useEffect, useState } from "react"
import { useSnackbar } from "../../../utils/notificacoes"
import { EdicaoClienteActionsTypes } from "../context/edicaoClienteActions"
import { useEdicaoClienteContext } from "../context/useEdicaoClienteContext"
import type { CampoEditavel } from "../models/edicaoClienteModel"
import { isDataValida } from "../models/edicaoClienteModel"
import { useCardStyles } from "./estilosCards"

export function FormularioCliente() {
    const classes = useCardStyles()
    const { dados, dispatch, salvar } = useEdicaoClienteContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const [nascimentoStr, setNascimentoStr] = useState(dados.dataNascimento)

    useEffect(() => {
        setNascimentoStr(dados.dataNascimento)
    }, [dados.dataNascimento])

    const handleCampo = (campo: CampoEditavel, valor: string | boolean) => {
        dispatch({ type: EdicaoClienteActionsTypes.ATUALIZAR_CAMPO, payload: { campo, valor } })
    }

    const commitNascimento = () => {
        if (nascimentoStr === dados.dataNascimento) return
        if (nascimentoStr && !isDataValida(nascimentoStr)) {
            alertStart("Data de nascimento inválida.", "error")
            setNascimentoStr(dados.dataNascimento)
            return
        }
        handleCampo("dataNascimento", nascimentoStr)
    }

    const handleSalvar = async () => {
        if (!dados.nome.trim()) {
            alertStart("O nome do cliente é obrigatório.", "error")
            return
        }
        if (!dados.email.trim()) {
            alertStart("O e-mail do cliente é obrigatório.", "error")
            return
        }
        if (dados.dataNascimento && !isDataValida(dados.dataNascimento)) {
            alertStart("Data de nascimento inválida.", "error")
            return
        }

        try {
            const mensagem = await salvar()
            alertStart(mensagem, "success")
        } catch (error) {
            alertStart((error as Error).message || "Erro ao atualizar o cliente", "error")
        }
    }

    return (
        <Paper className={classes.configCard}>
            <Box className={classes.cardHeader}>
                <PersonOutline className={classes.iconHeader} />
                <Typography variant="h6" className={classes.cardHeaderTitle}>
                    Editar cadastro do cliente
                </Typography>
            </Box>

            <Box className={classes.cardContent}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Nome"
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            value={dados.nome}
                            onChange={e => handleCampo("nome", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="CPF"
                            variant="outlined"
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: 14 }}
                            value={dados.cpf}
                            onChange={e => handleCampo("cpf", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="E-mail"
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            inputProps={{ maxLength: 120 }}
                            value={dados.email}
                            onChange={e => handleCampo("email", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Telefone"
                            variant="outlined"
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: 11 }}
                            value={dados.telefone}
                            onChange={e => handleCampo("telefone", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Data de Nascimento"
                            type="date"
                            variant="outlined"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={nascimentoStr}
                            onChange={e => setNascimentoStr(e.target.value)}
                            onBlur={commitNascimento}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Taxa de entrega"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={dados.taxaEntrega}
                            onChange={e => handleCampo("taxaEntrega", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Observação"
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            value={dados.observacao}
                            onChange={e => handleCampo("observacao", e.target.value)}
                        />
                    </Grid>

                    {dados.permissoes.includes("marcar_cliente_vip") && (
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        color="primary"
                                        checked={dados.vip}
                                        onChange={e => handleCampo("vip", e.target.checked)}
                                    />
                                }
                                label="Marcar como vip"
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={dados.freteGratis}
                                    onChange={e => handleCampo("freteGratis", e.target.checked)}
                                />
                            }
                            label="Habilitar o frete grátis"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={dados.pontuarNaFidelidade}
                                    onChange={e =>
                                        handleCampo("pontuarNaFidelidade", e.target.checked)
                                    }
                                />
                            }
                            label="Pontuar na fidelidade"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={dados.receberSpam}
                                    onChange={e => handleCampo("receberSpam", e.target.checked)}
                                />
                            }
                            label="Receber notificações de divulgação no whatsapp"
                        />
                    </Grid>

                    <Grid item xs={12} style={{ textAlign: "center" }}>
                        <Button
                            variant="contained"
                            className={classes.botaoSalvar}
                            onClick={handleSalvar}
                        >
                            Atualizar
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Paper>
    )
}
